import { fromJS, Map, Set } from "immutable";
import { device } from "signoxe/api/schema";
import { optionalToJS } from "./utils";

const deviceFromId = (state, id) =>
  state.entities.getIn(["devices", id.toString()], Map());

const devicesFromIds = (state, ids) => ids.map((id) => deviceFromId(state, id));

const deviceScreenShotsFromDeviceId = (state, id) =>
  state.entities
    .get("deviceScreenShots", Map())
    .filter((screenShot) => screenShot.get("device") === id)
    .sortBy((sc) => sc.get("timestamp"))
    .reverse()
    .toList();

const deviceWithScreenShotsFromId = (state, id) =>
  deviceFromId(state, id).set(
    "screenShots",
    deviceScreenShotsFromDeviceId(state, id)
  );

const unassignedDevices = (state) =>
  devicesFromIds(
    state,
    state.devices.ids.filter(
      (id) => deviceFromId(state, id).get("group") === null
    )
  );

const groupFromId = (state, id) =>
  state.entities.getIn(["deviceGroups", id.toString()]);

const feedFromId = (state, id) =>
  state.entities.getIn(["feeds", id.toString()]);

const deviceGroupFromId = (state, id) => {
  if (state.deviceGroups.ids.includes(id)) {
    return groupFromId(state, id)
      .update("devices", (devices) => devicesFromIds(state, devices))
      .update("feed", (feed) => feedFromId(state, feed))
      .update("schedule", (schedule) => scheduleFromIds(state, schedule))
      .update("daily_schedule", (daily_schedule) => dailyScheduleFromIds(state, daily_schedule))
      .update("specials", (specials) => specialContentFromIds(state, specials));
  }
};

const deviceGroupWraperFromId = (state, id) => {
  if (state.deviceUngroupWrapers.ids.includes(id)) {
    return groupFromId(state, id)
      .update("devices", (devices) => devicesFromIds(state, devices))
      .update("feed", (feed) => feedFromId(state, feed))
      .update("schedule", (schedule) => scheduleFromIds(state, schedule))
      .update("daily_schedule", (daily_schedule) => dailyScheduleFromIds(state, daily_schedule))
      .update("specials", (specials) => specialContentFromIds(state, specials));
  }
};

const deviceGroupsAll = (state) =>
  state.deviceGroups.ids.map((id) => deviceGroupFromId(state, id));

const deviceUngroupWrapersAll = (state) =>
  state.deviceUngroupWrapers.ids.map((id) => deviceGroupWraperFromId(state, id));

const devicesAll = (state) =>
  state.devices.ids.map((id) => deviceFromId(state, id));

const scheduleFromId = (state, id) =>
  state.entities
    .getIn(["scheduledContent", id.toString()])
    .update("content", (content) => feedFromId(state, content));

const scheduleFromIds = (state, ids) =>
  ids
    .map((id) => scheduleFromId(state, id))
    .groupBy((sch) => sch.get("day"))
    .update((sch) =>
      sch.count() > 0 ? sch.set("present", true) : sch.set("present", false)
    );

const specialContentFromId = (state, id) =>
  state.entities
    .getIn(["specialContent", id.toString()])
    .update("content", (content) => feedFromId(state, content));

const specialContentFromIds = (state, ids) =>
  ids.map((id) => specialContentFromId(state, id));

const dailyScheduleFromId = (state, id) =>
  state.entities
    .getIn(["scheduledDailyContent", id.toString()])
    .update("content", (content) => feedFromId(state, content));

const dailyScheduleFromIds = (state, ids) =>
    ids.map((id) => dailyScheduleFromId(state, id));

const pick = (map, keys) => map.filter((value, key) => Set(keys).has(key));

export const processScheduleForBulkUpdate = (schedule) =>
  fromJS(schedule)
    .filter((entry) => !entry.get("default"))
    .map((entry) => pick(entry, ["id", "start_time", "end_time"]))
    .toJS();

export const processDailyScheduleForBulkUpdate = (daily) =>
  fromJS(daily)
    .filter((entry) => !entry.get("default"))
    .map((entry) => pick(entry, ["id", "start_time", "end_time"]))
    .toJS();

export const getUnassignedDevices = (state) => unassignedDevices(state).toJS();

export const getDeviceGroups = (state) => deviceGroupsAll(state).toJS();

export const getDeviceGroupWraper = (state, id) =>
  optionalToJS(deviceGroupWraperFromId(state, id));

export const getDeviceGroup = (state, id) =>
  optionalToJS(deviceGroupFromId(state, id));

export const getDeviceWithScreenShots = (state, id) =>
  deviceWithScreenShotsFromId(state, id).toJS();

export const getDevices = (state) => {
  let allDevices = deviceGroupsAll(state).toJS();
  allDevices = allDevices.map((deviceGroup) => deviceGroup.devices).flat();
  return allDevices;
};

// search logic
const deviceMatchesQuery = (device, query, deviceTags) => {
  let devices_obj = device.get("devices").toJS();

  // Check if any device in the devices array matches the query by name or tag
  let devicesArrayMatch = devices_obj.some(deviceItem => {
    let nameMatch = deviceItem.name.toLowerCase().includes(query.toLowerCase());

    // Check if any of the device's tags match
    let tagMatch = deviceItem.tags.some(tagId => {
      let tagMatch_bool = false
      deviceTags.forEach(tag => {
        if(tag.id == tagId){
          console.log(tag.name)
          tagMatch_bool = tag.name.toLowerCase().includes(query.toLowerCase())
        }
      });
      
      return tagMatch_bool;
    });

    return nameMatch || tagMatch;
  });

  const mainDeviceMatch = device.get("name").toLowerCase().includes(query.toLowerCase());

  return mainDeviceMatch || devicesArrayMatch;
};



const deviceFilterFunction = (query, deviceTags) => (device) => deviceMatchesQuery(device, query, deviceTags)

const filteredDevicesByQuery = (state, query, deviceTags) => {
  let filteredDevices = deviceGroupsAll(state);

  query = query.trim().toLowerCase();

  if (query !== "") {
    filteredDevices = filteredDevices.filter(
      deviceFilterFunction(query, deviceTags)
    );
  }

  return filteredDevices;
};

const filteredDevicesWrapersByQuery = (state, query, deviceTags) => {
  let filteredDevices = deviceUngroupWrapersAll(state);

  query = query.trim().toLowerCase();

  if (query !== "") {
    filteredDevices = filteredDevices.filter(
      deviceFilterFunction(query, deviceTags)
    );
  }

  return filteredDevices;
};

const filteredDevices = (state, query, deviceTags) => {
  return filteredDevicesByQuery(state, query, deviceTags);
};

const filteredDevicesWrapers = (state, query, deviceTags) => {
  return filteredDevicesWrapersByQuery(state, query, deviceTags);
};

export const getFilteredDevices = (state, query, deviceTags) => filteredDevices(state, query, deviceTags).toJS();
export const getFilteredDevicesWrapers = (state, query, deviceTags) => filteredDevicesWrapers(state, query, deviceTags).toJS();

export const isFetchingDevices = (state) => state.devices.isFetching;
export const getDeviceGroupspreloadState = (state) => state.paginationState.devicePage.nextState;



// WEBPACK FOOTER //
// ./node_modules/source-map-loader!./src/selectors/devices.js